/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { addDays, differenceInCalendarDays, isFuture, formatDistanceToNow, format } from 'date-fns';
import mbpLogger from 'mbp-logger';
import { useFormContext } from 'react-hook-form';

// MUI
import {
    Grid, Button, Divider, CircularProgress,
} from '@material-ui/core';

// Helper
import { RecipientBookContext } from '../../Context/RecipientBookContext';
import { occasionsMapping, unmaskPhoneNumber } from '../../Helpers/Helpers';
import { getUserProfile } from '../../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { isEmpty } from '../../../../../../../../pages/Account/utils/object';
import { getBrand } from '../../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

// API
import { processUpdateSingleOccasionEntry } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/updateSingleOccasionEntry';
import { processDeleteSingleOccasionEntry } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/deleteSingleOccasionEntry'
import { processAddOccasionEntries } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/processAddOccasionEntries';

// Component
import AddOccasionsForm from '../../AddNewRecipient/AddOccasions/AddOccasionsForm';
import DeleteDialog from '../DeleteDialog'

// Icons
import confetti from '../../Assets/confetti.svg';
import RecipientBookBrandColorIcons from '../../Helpers/RecipientBookBrandColorIcons';
import closeIcon from '../../Assets/filterCloseIcon.svg';
// import StyledButton from '../../../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

const EditOccasions = ({
    classes, occ, index, rcpId, occasions, rcpFirstName, rcpLastName, sortedOccasions, isParentDesktopComponent, selectedOccasionId
}) => {
    const {
        currentRecipient,
        setCurrentRecipient,
        isOccasionsEditing,
        setIsOccasionsEditing,
        isAddressEditing,
        setIsAddressEditing,
        isProfileEditing,
        setIsProfileEditing,
        selectedOccasionToEdit,
        setSelectedOccasionToEdit,
        setSelectedAddressToEdit,
        setAddNewAddressClicked,
        addNewAddressClicked,
        closeBtnClicked,
        setCloseBtnClicked,
        showMoreOccasions,
        isReturnToRecipientListOnMobileClicked,
        setShowMoreOccasions,
    } = useContext(RecipientBookContext);
    const [error, setError] = useState('');
    const [occasionError, setOccasionError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isShowingDeleteDialog, setIsShowingDeleteDialog] = useState(false);
    const [switchStatus, setSwitchStatus] = useState(); // Note: Do not remove this state, this is required to update occasions on switch status on switch change
    const [editBtnClicked, setEditBtnClicked] = useState(false);
    const methods = useFormContext();
    const dispatch = useDispatch();
    
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const expanded = queryParams.get('expanded');

    const userProfile = useSelector(getUserProfile) || {};
    const brandId = useSelector(getBrand).id;
    const { ContactId, firstName, lastName } = userProfile || '';
    const holidayOccasion = Number(occ.occasion) >= 5 && Number(occ.occasion) <= 21;

    const getStatusOfSwitch = (switchStatus, passedIndex) => {
        methods.setValue(`occasions[${index}].subscribedEmail`, switchStatus);
        setSwitchStatus(switchStatus);
        if (switchStatus === false) {
            methods.setValue(`occasions[${index}].subscribedPhone`, switchStatus);
        };
    };

    const handleEditOccasion = (occasionData) => {
        setIsProfileEditing(false); // setting global profile state true to keep edit profile form open
        setIsAddressEditing(false);
        setAddNewAddressClicked(false);
        setSelectedAddressToEdit('');
        setShowMoreOccasions(false);
        setSelectedOccasionToEdit(occasionData?.occasion);
        setIsOccasionsEditing(true); // setting global occasion state true to keep edit occasion form open

        // Reset occasion fields
        if (selectedOccasionToEdit && selectedOccasionToEdit !== occ.occasion) {
            methods.reset({ occasions: sortedOccasions });
        }
    };

    useEffect(() => {
        if (expanded && selectedOccasionId !== '' && !isOccasionsEditing && !isProfileEditing && !isAddressEditing && !addNewAddressClicked && !closeBtnClicked && !showMoreOccasions && !isReturnToRecipientListOnMobileClicked && !editBtnClicked) {
            const selectedOccToEdit = occasions.find((occasion) => occasion.occasionId === selectedOccasionId);
            handleEditOccasion(selectedOccToEdit);
        }
    }, []);

    useEffect(() => {
        if (selectedOccasionToEdit === '' && occ.occasion === '2' && occ.dateMM === '' && occ.dateDD === '' && !isProfileEditing && !isAddressEditing && !addNewAddressClicked && !closeBtnClicked && !showMoreOccasions && !isReturnToRecipientListOnMobileClicked) {
            handleEditOccasion(occ);
        }
    }, []);

    const updateRecipient = (updatedOccasion) => {
        // Create a copy of the recipient data
        // const updatedRecipientData = { ...recipientState };
        const { addressBookEntryId, dateDD, dateMM, dateYYYY, occasion, occasionId, rcpId, desc, createdUser, subscribedEmail, reminderEmail, subscribedPhone, reminderPhone } = updatedOccasion;
        // Support for Desktop
        // Mobile needs to be refactored to use currentRecipient
        if (!isEmpty(currentRecipient)) {
            let currentRecipientTargetIndex = -1;
            if (currentRecipient.occasions.length) {
                currentRecipientTargetIndex = currentRecipient?.occasions?.findIndex((occasion) => occasion.occasion === updatedOccasion.occasion);
            } else {
                currentRecipient['occasions'] = [{ addressBookEntryId, dateDD, dateMM, dateYYYY, occasion, occasionId, rcpId, desc, createdUser }]
            }

            if (currentRecipientTargetIndex !== -1) {
                currentRecipient.occasions[currentRecipientTargetIndex] = {
                    addressBookEntryId,
                    dateDD,
                    dateMM,
                    dateYYYY,
                    occasion,
                    occasionId,
                    rcpId,
                    desc,
                    createdUser,
                    subscribedEmail,
                    reminderEmail,
                    subscribedPhone,
                }
            }
            currentRecipient.occasions.map((occasion) => occasion.reminderPhone = reminderPhone);
            setCurrentRecipient(currentRecipient)
        }
    };

    const addOccasionToRecipient = (newOccasion) => {
        const { addressBookEntryId, dateDD, dateMM, dateYYYY, occasion, occasionId, rcpId, desc, createdUser, subscribedEmail, reminderEmail, subscribedPhone, reminderPhone } = newOccasion;

        // Support for Desktop
        // Mobile needs to be refactored to use currentRecipient
        if (!isEmpty(currentRecipient)) {
            let currentRecipientTargetIndex = -1;
            if (currentRecipient.occasions?.length) {
                currentRecipientTargetIndex = occasions?.findIndex((occ) => occ.occasion === occasion);
            } else {
                currentRecipient['occasions'] = [{ addressBookEntryId, dateDD, dateMM, dateYYYY, occasion, occasionId, rcpId, desc, createdUser, subscribedEmail, subscribedPhone, reminderEmail, reminderPhone }]
            }
            if (currentRecipientTargetIndex !== -1) {
                const filterOccasions = occasions?.filter((occ) => occ.occasion !== newOccasion.occasion)
                currentRecipient.occasions = [
                    ...filterOccasions,
                    {
                        addressBookEntryId,
                        dateDD,
                        dateMM,
                        dateYYYY,
                        occasion,
                        occasionId,
                        rcpId,
                        desc,
                        createdUser,
                        subscribedEmail,
                        reminderEmail,
                        subscribedPhone,
                    }
                ]
            }
            currentRecipient.occasions.map((occasion) => occasion.reminderPhone = reminderPhone);
            setCurrentRecipient(currentRecipient);
        }
    };

    const updateSingleOccasionEntryMutation = useMutation({
        mutationFn: processUpdateSingleOccasionEntry,
        onSuccess: (data) => {
            if (data.status === 200 && data && data.data !== undefined) {
                const updatedOccasion = data?.data?.[0];
                updateRecipient(updatedOccasion);
                methods.reset({}, { keepValues: true, keepDirty: false }); // to keep submit button disable after submitting current change 
            }
            setLoading(false);
            setSelectedOccasionToEdit('');
            setIsOccasionsEditing(false);
        },
        onError: (err) => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            mbpLogger.logError({
                function: 'updateSingleOccasionEntryMutation',
                appName: process.env.npm_package_name,
                module: 'EditOccasions.js',
                message: `Error While Updating an Occasion with Id: ${occ.occasion} for Recipient with Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const postOccasionEntriesMutation = useMutation({
        mutationFn: processAddOccasionEntries,
        onSuccess: (data) => {
            if (data.status === 200 && data && data.data !== undefined) {
                const newOccasion = data.data[0];
                addOccasionToRecipient(newOccasion);
            }
            setLoading(false);
            setSelectedOccasionToEdit('');
            setIsOccasionsEditing(false);
            methods.reset({}, { keepValues: true, keepDirty: false });
        },
        onError: (err) => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            mbpLogger.logError({
                function: 'updateSingleOccasionEntryMutation',
                appName: process.env.npm_package_name,
                module: 'EditOccasions.js',
                message: `Error While Updating an Occasion with Id: ${occ.occasion} for Recipient with Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const deleteSingleOccasionEntryMutation = useMutation({
        mutationFn: processDeleteSingleOccasionEntry,
        onSuccess: (data) => {
            if (data && data.data !== undefined) {
                if (data.status === 200) {
                    const url = data?.config?.url || '';
                    const parts = url.split('/');
                    const occasionId = parts?.[parts?.length - 1];
                    if (!isEmpty(currentRecipient)) {
                        const filteredCurrentRecipientOccasions = currentRecipient?.occasions?.filter((occ) => occ.occasionId !== occasionId)
                        setCurrentRecipient({ ...currentRecipient, occasions: filteredCurrentRecipientOccasions })
                    }
                }
            }
            setLoading(false);
            setSelectedOccasionToEdit('');
            setIsOccasionsEditing(false);
        },
        onError: (err) => {
            setOccasionError('Something went wrong. Please try again.');
            setLoading(false);
            mbpLogger.logError({
                function: 'removeSingleOccasionEntryMutation',
                appName: process.env.npm_package_name,
                module: 'EditOccasions.js',
                message: `Error While Deleting an Occasion with Id: ${occ.occasion} for Recipient with Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const updateOccasionEntry = async (updateOccasionPayload) => {
        dispatch(trackEvent({
            eventName: "contact_edit_interaction",
            edit_action_type: "Edit Occasion",
            occasion: updateOccasionPayload[0].desc,
            eventCategory: "Address Book",
            eventAction: "Contact Edit",
            eventLabel: `Occasion Edit - ${updateOccasionPayload[0].desc}`,
        }))
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        updateSingleOccasionEntryMutation.mutate({
            ContactId,
            data: updateOccasionPayload,
            occasionId: occ.occasionId,
            jwtToken,
            addressBookEntryId: occ.addressBookEntryId,
        });
    };

    const postOccasionEntries = async (occasionPayload) => {
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        dispatch(trackEvent({
            eventName: "contact_edit_interaction",
            edit_action_type: "Edit Occasion",
            occasion: occasionPayload[0].desc,
            eventCategory: "Address Book",
            eventAction: "Contact Edit",
            eventLabel: `Occasion Add - ${occasionPayload[0].desc}`
        }))
        postOccasionEntriesMutation.mutate({
            ContactId,
            data: occasionPayload,
            jwtToken,
        });
    };

    const deleteOccasionEntry = async () => {
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        dispatch(trackEvent({
            eventName: "contact_edit_interaction",
            edit_action_type: "Delete Occasion",
            occasion: occ.desc,
            eventCategory: "Address Book",
            eventAction: "Contact Edit",
            eventLabel: `Occasion Delete - ${occ.desc}`
        }))
        deleteSingleOccasionEntryMutation.mutate({
            ContactId,
            occasionId: occ.occasionId,
            jwtToken,
            addressBookEntryId: occ.addressBookEntryId,
        });
    };

    const onSubmit = (data) => {
        const { dateDD, dateMM, dateYYYY, desc, occasion, subscribedEmail, subscribedPhone, reminderPhone } = data.occasions[index];

        // Error handling for Birthday
        if (selectedOccasionToEdit === '2' && ((!dateMM && !dateDD) || (!dateMM && dateDD))) {
            return methods.setError(`occasions[${index}]`, {
                message: 'Month is required'
            })
        } else if (selectedOccasionToEdit === '2' && dateMM && !dateDD) {
            return methods.setError(`occasions[${index}]`, {
                message: 'Day is required'
            })
        }

        const updatedSubscribedEmail = subscribedEmail || false;
        if (occ.occasionId && occasion === occ.occasion) {
            const updateOccasionPayload = [{
                rcpFirstName,
                rcpLastName,
                custFirstName: firstName,
                custLastName: lastName,
                rcpId,
                occasion: occ.occasion,
                dateDD,
                dateMM,
                dateYYYY: dateYYYY?.toString(),
                createdUser: "SSP",
                reminderEmail: userProfile.email || '',
                reminderPhone: unmaskPhoneNumber(reminderPhone || ''),
                subscribedEmail: updatedSubscribedEmail || false,
                subscribedPhone: subscribedPhone || false,
                desc: occ.desc,
                isOneDay: occ.isOneDay,
                isThreeDays: occ.isThreeDays,
                isOneWeek: occ.isOneWeek,
                isTwoWeek: occ.isTwoWeek,
                isOneMonth: occ.isOneMonth,
                brandId
            }];
            return updateOccasionEntry(updateOccasionPayload);
        }
        const addOccasionsPayload = [
            {
                rcpFirstName,
                rcpLastName,
                custFirstName: firstName,
                custLastName: lastName,
                dateDD,
                dateMM,
                dateYYYY: dateYYYY?.toString(),
                occasion,
                desc,
                rcpId,
                createdUser: "SSP",
                reminderEmail: userProfile.email || '',
                reminderPhone: unmaskPhoneNumber(reminderPhone || ''),
                subscribedEmail: updatedSubscribedEmail || false,
                subscribedPhone: subscribedPhone || false,
                isOneDay: occ.isOneDay,
                isThreeDays: occ.isThreeDays,
                isOneWeek: occ.isOneWeek,
                isTwoWeek: occ.isTwoWeek,
                isOneMonth: occ.isOneMonth,
                brandId,
            }
        ]
        postOccasionEntries(addOccasionsPayload)
    };

    const onError = (err) => {
        console.log('err', err);
    }

    const occasionWithDates = (occ.dateMM?.length && occ.dateDD?.length) ? true : false;
    const { formState: { dirtyFields } } = methods;
    // Even though it is not used and I remove it - checkbox is breaking. Need to troubleshoot
    // This should be fixed in my push - I'm leaving it commented in for now though so we can test my changes first - Milo
    console.log('[+++]', dirtyFields)
    const occasionDate = occasionWithDates ? new Date(`${occ.dateMM}/${occ.dateDD}/${new Date().getFullYear()}`) : '';
    const formattedOccasionDate = occasionDate !== '' ? format(occasionDate, 'MMM. do') : '';

    const getHowManyDaysAway = () => {
        if (!occ.dateDD && !occ.dateMM) return;
        const occasionDate = occasionWithDates ? new Date(`${occ.dateMM}/${occ.dateDD}/${new Date().getFullYear()}`) : '';
    
        // Goal for showing days away is to find the NEXT OCCURRENCE
        // if occasion date is in the future, we can use the occasion date as is
        // if occasion date is in the past, we have to increment it to put it into the future in order to find the next occurrence.
        const occasionDateWithAdjustedForYear = isFuture(occasionDate) ? occasionDate : new Date(`${occ.dateMM}/${occ.dateDD}/${new Date().getFullYear() + 1}`);
    
        const daysAway = differenceInCalendarDays(occasionDateWithAdjustedForYear, new Date());
    
        if (daysAway <= 31) {
            const adjustedDate = addDays(new Date(), daysAway);
            const formattedDistance = formatDistanceToNow(adjustedDate, { addSuffix: true });
            
            return (
                <div style={{ fontSize: '13px', display: 'flex', alignItems: 'end', gap: '0.25rem' }}>
                    <p style={{margin: '0'}}>{formattedDistance}</p>
                    <img src={confetti} alt="confetti" />
                </div>
            );
        }
    
        return null;
    };
    return (
        <Grid container item xs={12}>
            {
                <>
                    <Grid container item xs={12} className={classes.addNewRecipientWrapper} alignItems='center'>
                        <Grid container item xs={12} md={3}>
                            <Grid container item xs={10} alignItems='center'>
                                <RecipientBookBrandColorIcons className={classes.iconStyle} icon={occasionsMapping?.[occ.occasion]?.name} strockClass={classes.iconStyleStroke} />
                                <span style={{ marginLeft: '8px' }} className={classes.occasionName}>{occasionsMapping?.[occ.occasion]?.name}</span>
                            </Grid>
                            {!isParentDesktopComponent && (
                                <>
                                    {(occasions?.[index]?.occasion !== selectedOccasionToEdit)
                                        ? (<Grid container item xs={2} style={{ justifyContent: 'end' }}>
                                            <button
                                                type="button"
                                                style={{ borderRadius: '0px', marginRight: '0px', cursor: 'pointer' }}
                                                className={classes.editBtn}
                                                onClick={() => {
                                                    handleEditOccasion(occ);
                                                    setEditBtnClicked(true);
                                                }}>Edit</button>
                                        </Grid>)
                                        : (<Grid container item xs={2} style={{ justifyContent: 'end' }}>
                                            <div
                                                className={classes.closeIcon}
                                                onClick={() => {
                                                    setCloseBtnClicked(true);
                                                    setIsOccasionsEditing(false);
                                                    setSelectedOccasionToEdit('');
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={() => { }}>
                                                <span>
                                                    <img src={closeIcon} alt="close icon" />
                                                </span>
                                            </div>
                                        </Grid>)
                                    }
                                </>
                            )}
                        </Grid>
                        {(occasions?.[index]?.occasion !== selectedOccasionToEdit) && (
                            <Grid container xs={12} md={7} spacing={2} style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                                {occasionDate
                                    && (
                                        <Grid container item xs={4} md={4} spacing={2} style={{ fontSize: '14px' }}>
                                            <Grid item xs={12}>
                                                <span style={{ fontSize: '16px' }}>{formattedOccasionDate}</span>
                                            </Grid>
                                        </Grid>
                                    )}
                                <Grid item xs={8} md={4}>
                                    {getHowManyDaysAway()}
                                </Grid>
                            </Grid>
                        )}
                        {isParentDesktopComponent && (
                            <>
                                {(occasions?.[index]?.occasion !== selectedOccasionToEdit)
                                    ? (
                                        <Grid item xs={2}>
                                            <button
                                                type="button"
                                                style={{ borderRadius: '0px', marginRight: '16px', cursor: 'pointer' }}
                                                className={classes.editBtn}
                                                onClick={() => {
                                                    handleEditOccasion(occ);
                                                    setEditBtnClicked(true);
                                                }}
                                            >Edit</button>
                                        </Grid>
                                    )
                                    : (<Grid container item xs={9} style={{ justifyContent: 'end' }}>
                                        <div
                                            className={classes.closeIcon}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                setCloseBtnClicked(true);
                                                setIsOccasionsEditing(false);
                                                setSelectedOccasionToEdit('');
                                            }}
                                            onKeyDown={() => { }}
                                        >
                                            <span><img src={closeIcon} alt="close icon" /></span>
                                        </div>
                                    </Grid>)
                                }
                            </>
                        )}
                        {occasions?.[index]?.occasion === selectedOccasionToEdit && isOccasionsEditing && (
                            <Grid item xs={12}>
                                <Grid container item className="formStyle">
                                    <form style={{ width: '100%' }}>
                                        <AddOccasionsForm
                                            rcpFirstName={rcpFirstName}
                                            rcpLastName={rcpLastName}
                                            classes={classes}
                                            occasion={occ}
                                            showDivider={false}
                                            index={index}
                                            methods={methods}
                                            holidayOccasion={holidayOccasion}
                                            getStatusOfSwitch={getStatusOfSwitch}
                                            switchStatus={switchStatus}
                                        />
                                        {error && (
                                            <Grid container justifyContent="center" alignItems="center">
                                                <p style={{ color: 'red', marginBottom: '0px' }}>{error}</p>
                                            </Grid>
                                        )}
                                        <Grid container direction="row" justifyContent="center" alignItems="center" style={{ marginTop: '16px', marginBottom: '16px' }}>
                                            <Grid container item xs={12} md={5} lg={5} justifyContent="center" alignItems="center">
                                                {occ?.occasionId && (
                                                    <Grid item xs={2} md={2} lg={2}>
                                                        <Button className={classes.deleteBtnIcon} onClick={() => setIsShowingDeleteDialog(true)}>
                                                            <RecipientBookBrandColorIcons className={classes.deleteIcon} icon='DeleteIcon' strockClass={classes.deleteIconStroke} />
                                                        </Button>
                                                        <DeleteDialog
                                                            classes={classes}
                                                            loading={loading}
                                                            error={occasionError}
                                                            setError={setOccasionError}
                                                            isShowingDeleteDialog={isShowingDeleteDialog}
                                                            setIsShowingDeleteDialog={setIsShowingDeleteDialog}
                                                            handleClick={deleteOccasionEntry}
                                                            description="Occasion"
                                                        />
                                                    </Grid>
                                                )}
                                                {(
                                                    <Grid item xs={occ?.occasionId ? 10 : 12} md={8} lg={8}>
                                                        <Button
                                                            fullWidth
                                                            variant='contained'
                                                            className={classes.commonBtnContained}
                                                            onClick={methods.handleSubmit(onSubmit, onError)}
                                                            disabled={loading}
                                                            style={isParentDesktopComponent ? { width: '244px', height: '46px', margin: '0px 0px 0px 24px', float: 'none', fontSize: '16px' } : { width: '100%', height: '46px' }}
                                                        >
                                                            {loading ? <CircularProgress /> : 'Save'}
                                                        </Button>
                                                        {
                                                            /*
                                                            This EDS button styles are being overriden by the button styles inside main.js
                                                            
                                                            We should figure out what is protecting other buttons from inheriting the styles inside main.js but this button
                                                            is not protected. Disabling this button for now.
                                                            */
                                                        }
                                                        {/* <StyledButton
                                                                fullWidth
                                                                variant="primary"
                                                                handleOnClick={methods.handleSubmit(onSubmit, onError)}
                                                                disabled={loading || isDisable}
                                                                label={loading ? <CircularProgress /> : 'Save'}
                                                                size={'md'}
                                                                type="submit"
                                                            /> */}
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                    <Divider className={classes.dividerStyle} />
                </>
            }
        </Grid>
    );
};

export default EditOccasions;
